import { lazy, Suspense, useContext } from 'react';
import { Helmet } from 'react-helmet-async';

import { IssueContext } from '@entities/issue';
import {
	ReaderStateContext,
	loadResponsive,
	loadReplica,
	useIssueOpenEvent,
} from '@widgets/issue-reader';
import { DOWNLOADED_ISSUE_BASE_PATH } from '@entities/downloaded-issue';
import type { TopbarExtraButtons } from '@shared/ui/topbar';
import { DocumentHead } from '@shared/ui/document-head';

import type { UseIssueViewResult } from '../model/use-issue-view';
import { Loading } from './loading';
import { IssueUnavailableToast } from './issue-unavailable-toast';

const LazyAudioPlayer = lazy(() => import('@widgets/player'));
const LazyResponsive = lazy(loadResponsive);
const LazyReplica = lazy(loadReplica);

/**
 * The issue-reading page's UI shell: everything about *how* `useIssueView`'s
 * result gets composed into a screen, shared by `@pages/issue`'s own `Issue`
 * and any template's own issue page (improvements/
 * 07.1-per-template-issue-reader-widgets.md). `extraButtons` is the one
 * customization point today -- a template passes tenant-specific navbar
 * buttons through to whichever reader mode (`Responsive`/`Replica`) is
 * active. A template that needs to differ by more than that (a different
 * widget arrangement entirely) composes `useIssueView`'s result directly
 * instead of using this shell -- that's the intended escape hatch, not a
 * reason to grow this component's parameter list speculatively.
 */
export function IssueReaderShell(props: IssueReaderShellProps) {
	const { view, extraButtons } = props;
	const {
		documentMeta,
		issue,
		currentArticle,
		stylesMap,
		isUnavailableOffline,
		mode,
		issueContextValue,
		readerStateValue,
	} = view;

	// The effect that navigates away is already running inside `useIssueView`
	// by the time this is true -- just show the loading state underneath the
	// toast for that window, rather than rendering content for a route we're
	// about to leave.
	// Before the early returns (hooks cannot follow one). It waits for the
	// reader to be ready, so an issue that cannot be shown is not "opened".
	const ready =
		!isUnavailableOffline && !!currentArticle && !!stylesMap && !!issue;
	useIssueOpenEvent({
		issue: ready ? issue : null,
		language: issueContextValue.language,
		downloaded: issueContextValue.basePath === DOWNLOADED_ISSUE_BASE_PATH,
	});

	if (isUnavailableOffline) {
		return (
			<>
				<IssueUnavailableToast open={isUnavailableOffline} />
				<Loading />
			</>
		);
	}

	if (!currentArticle || !stylesMap || !issue) {
		return <Loading />;
	}

	return (
		<IssueContext.Provider value={issueContextValue}>
			<ReaderStateContext.Provider value={readerStateValue}>
				<DocumentHead
					title={documentMeta.title}
					description={documentMeta.description}
				/>
				<HeadHelmet />
				{/* `currentArticle`/`stylesMap`/`issue` being ready (the guard
				    above) says the *data* has arrived -- it says nothing
				    about whether `responsive.tsx`/`replica.tsx`'s own chunk
				    has finished loading yet. Without a Suspense boundary
				    here, that gap bubbles all the way up to the router's
				    root Suspense (`root-route.tsx`), which swaps the whole
				    outlet -- navbar included -- for the generic
				    `RoutePending` fallback for a frame, then back: a visible
				    flash. Falling back to `Loading` here instead means that
				    gap continues showing the exact same real-navbar
				    component already on screen from the phase just before
				    it, so there's nothing left to flash. */}
				<Suspense fallback={<Loading />}>
					<LazyAudioPlayer
						url={readerStateValue.audioPlayerUrl}
						onClose={() => {
							readerStateValue.setAudioPlayerUrl(null);
						}}
					>
						{mode === 'responsive' ? (
							<LazyResponsive extraButtons={extraButtons} />
						) : (
							<LazyReplica extraButtons={extraButtons} />
						)}
					</LazyAudioPlayer>
				</Suspense>
			</ReaderStateContext.Provider>
		</IssueContext.Provider>
	);
}

export interface IssueReaderShellProps {
	view: UseIssueViewResult;
	extraButtons?: TopbarExtraButtons;
}

function HeadHelmet() {
	const { currentArticle, language, issue } = useContext(IssueContext);
	const { role, name, thumbnail } = currentArticle ?? {};
	const keywords =
		currentArticle?.keywords && currentArticle.keywords.length
			? currentArticle.keywords
			: [];
	const keywordsString = [...new Set(keywords)].join(',');
	return (
		<Helmet>
			{issue?.id && <meta property="cf:issue" content={`${issue.id}`} />}
			{currentArticle?.id && (
				<meta property="cf:article" content={`${currentArticle.id}`} />
			)}
			<meta property="og:type" content={role} />
			{name?.[language] ? (
				<meta property="og:title" content={name?.[language]} />
			) : null}
			<meta property="og:url" content={window.location.href} />
			{thumbnail ? (
				<meta property="og:image" content={thumbnail} />
			) : null}
			<meta property="og:category" content="digital-editions" />
			{keywordsString.length ? (
				<meta name="keywords" content={keywordsString}></meta>
			) : null}
			{keywordsString.length ? (
				<meta name="post-tags" content={keywordsString}></meta>
			) : null}
		</Helmet>
	);
}
